import { useEffect, useState } from 'react';
import { Button, Icon, useToast } from './ui';
import { api } from '../lib/api';
import {
  PushError, enablePush, getPushStatus, isIos, isStandalone, pushOptedOut, pushSupported, type PushKeyResponse
} from '../lib/push';
import { tr } from '../lib/i18n';

const DISMISS_KEY = 'nailsly.pushPromptDismissedAt';
const IOS_HINT_KEY = 'nailsly.pushIosHintSeen';
const DISMISS_DAYS = 30;

export function PushPrompt({ businessId }: { businessId: string }): JSX.Element | null {
  const toast = useToast();
  const [view, setView] = useState<'hidden' | 'ask' | 'ios'>('hidden');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setView('hidden');
    setError(null);

    if (isIos() && !isStandalone()) {
      if (!localStorage.getItem(IOS_HINT_KEY)) setView('ios');
      return;
    }
    if (!pushSupported() || Notification.permission === 'denied' || pushOptedOut()) return;

    void (async () => {
      try {
        if (Notification.permission === 'granted') {
          // Permissão já liberada: inscreve este aparelho sem pedir nada de novo.
          const status = await getPushStatus(businessId);
          if (!cancelled && status.enabled && !status.deviceSubscribed) {
            await enablePush(businessId);
          }
          return;
        }
        const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) ?? 0);
        if (Date.now() - dismissedAt < DISMISS_DAYS * 86_400_000) return;
        const key = await api.get<PushKeyResponse>('/api/notifications/push/key');
        if (!cancelled && key.enabled && key.publicKey) setView('ask');
      } catch {
        /* Push indisponível ou sem rede: Configurações › Notificações segue como caminho manual. */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [businessId]);

  async function onEnable(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await enablePush(businessId);
      setView('hidden');
      toast(tr('Notificações ativadas neste aparelho.'), 'success');
    } catch (err) {
      if (err instanceof PushError && err.code === 'ios_install') setView('ios');
      else setError(err instanceof Error ? err.message : tr('Não foi possível ativar as notificações.'));
    } finally {
      setBusy(false);
    }
  }

  function onLater(): void {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* sem storage: pergunta de novo na próxima visita */
    }
    setView('hidden');
  }

  function onIosAck(): void {
    try {
      localStorage.setItem(IOS_HINT_KEY, '1');
    } catch {
      /* sem storage: mostra de novo na próxima visita */
    }
    setView('hidden');
  }

  if (view === 'hidden') return null;

  if (view === 'ios') {
    return (
      <section className="nl-card mb-4 flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:p-5" aria-label={tr("Notificações no iPhone")}>
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-accent text-on-accent">
          <Icon name="bell" size={20} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-graphite">{tr("Notificações no iPhone e no iPad")}</p>
          <p className="mt-0.5 text-xs leading-relaxed text-muted">
            {tr("No iOS, os avisos só funcionam com o AgendaPro na Tela de Início. Abra este site no Safari, toque em Compartilhar › Adicionar à Tela de Início, entre por lá e ative em Configurações › Notificações. No Android e no computador basta tocar em “Ativar notificações”.")}
          </p>
        </div>
        <div className="flex shrink-0 items-center">
          <Button variant="secondary" onClick={onIosAck}>{tr("Entendi")}</Button>
        </div>
      </section>
    );
  }

  return (
    <section className="nl-card mb-4 flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:p-5" aria-label={tr("Ativar notificações")}>
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-accent text-on-accent">
        <Icon name="bell" size={20} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-graphite">{tr("Ative as notificações de novos agendamentos")}</p>
        <p className="mt-0.5 text-xs leading-relaxed text-muted">
          {tr("Quando um cliente marcar pelo seu perfil, o aviso aparece neste aparelho — inclusive com o site fechado. Você pode desativar quando quiser em Configurações › Notificações.")}
        </p>
        {error ? <p className="mt-1 text-xs font-semibold text-danger">{error}</p> : null}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Button variant="ghost" onClick={onLater}>{tr("Agora não")}</Button>
        <Button onClick={() => void onEnable()} loading={busy} icon={<Icon name="bell" size={16} />}>
          {tr("Ativar notificações")}
        </Button>
      </div>
    </section>
  );
}
